// src/modules/goldInventory/components/GroupedMeltSection.jsx
// UI for the GROUPED batch type: source items (auto-derived from selected cases)
// are assigned either to a melted GROUP or kept STANDALONE. Each group is melted
// into one output item — user enters melting loss + assayed purity; gross (after
// loss), net weight and price (sum of members) are computed.

import React from 'react';
import {
  Card, Row, Col, Input, InputNumber, Select, Button, Typography, Tag, Empty, Divider,
} from 'antd';
import { PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

const n = (v) => Number(v) || 0;
const round3 = (v) => Math.round(n(v) * 1000) / 1000;
const round2 = (v) => Math.round(n(v) * 100) / 100;
const round4 = (v) => Math.round(n(v) * 10000) / 10000;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

// Aggregate a single group's members into merged figures.
export function groupAggregate(group, sourceItems) {
  const members = sourceItems.filter((s) => s.groupId === group.id);
  const sourceGross = round3(members.reduce((s, m) => s + n(m.grossWeight), 0));
  const mergedGross = round3(Math.max(0, sourceGross - n(group.meltingLoss)));
  const netWeight = round4((mergedGross * n(group.purityPercent)) / 100);
  const priceAmount = round2(members.reduce((s, m) => s + n(m.priceAmount), 0));
  return { members, sourceGross, mergedGross, netWeight, priceAmount };
}

// Build the final output items array (merged groups + standalone items) for the API.
// Returns { items, error }.
export function assembleGroupedItems(sourceItems, groups) {
  const items = [];

  for (const g of groups) {
    const agg = groupAggregate(g, sourceItems);
    if (agg.members.length === 0) {
      return { items: [], error: `Group "${g.label || 'Untitled'}" has no items assigned.` };
    }
    if (!(n(g.purityPercent) > 0)) {
      return { items: [], error: `Enter a purity % for group "${g.label || 'Untitled'}".` };
    }
    items.push({
      label: g.label || 'Merged',
      grossWeight: agg.mergedGross,
      purityPercent: n(g.purityPercent),
      priceAmount: agg.priceAmount,
      netWeight: agg.netWeight,
      isMerged: true,
      meltingLoss: n(g.meltingLoss),
      members: agg.members.map((m) => ({
        label: m.label,
        grossWeight: n(m.grossWeight),
        purityPercent: n(m.purityPercent),
        priceAmount: n(m.priceAmount),
      })),
    });
  }

  // Standalone items pass through as individual output items.
  sourceItems.filter((s) => !s.groupId).forEach((s) => {
    items.push({
      label: s.label,
      grossWeight: n(s.grossWeight),
      purityPercent: n(s.purityPercent),
      priceAmount: n(s.priceAmount),
      isMerged: false,
      meltingLoss: 0,
      members: [],
    });
  });

  if (items.length === 0) {
    return { items: [], error: 'Add at least one item (grouped or standalone).' };
  }
  return { items, error: null };
}

export default function GroupedMeltSection({ sourceItems, groups, onChangeSourceItems, onChangeGroups }) {
  const addGroup = () => {
    const nextNum = groups.length + 1;
    onChangeGroups([...groups, { id: `g${Date.now()}`, label: `Merged ${nextNum}`, meltingLoss: 0, purityPercent: null }]);
  };

  const updateGroup = (id, patch) => {
    onChangeGroups(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const removeGroup = (id) => {
    // Unassign members of this group back to standalone.
    onChangeSourceItems(sourceItems.map((s) => (s.groupId === id ? { ...s, groupId: null } : s)));
    onChangeGroups(groups.filter((g) => g.id !== id));
  };

  const assignItem = (key, groupId) => {
    onChangeSourceItems(sourceItems.map((s) => (s.key === key ? { ...s, groupId: groupId || null } : s)));
  };

  const groupOptions = [
    { value: '', label: 'Standalone' },
    ...groups.map((g) => ({ value: g.id, label: g.label || 'Merged' })),
  ];

  if (!sourceItems.length) {
    return <Empty description="Select cases above to load their gold items for grouping" style={{ margin: '16px 0' }} />;
  }

  const standaloneCount = sourceItems.filter((s) => !s.groupId).length;

  return (
    <div>
      <Divider orientation="left" plain>Source Items — assign each to a melt group or keep standalone</Divider>

      {/* Source items list */}
      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 640 }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={gTh}>Item</th>
              <th style={gTh}>Gross (g)</th>
              <th style={gTh}>Purity %</th>
              <th style={gTh}>Price (₹)</th>
              <th style={gTh}>Assign to</th>
            </tr>
          </thead>
          <tbody>
            {sourceItems.map((s) => (
              <tr key={s.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={gTd}>{s.label}</td>
                <td style={{ ...gTd, textAlign: 'right' }}>{round3(s.grossWeight).toFixed(3)}</td>
                <td style={{ ...gTd, textAlign: 'right' }}>{round2(s.purityPercent).toFixed(2)}</td>
                <td style={{ ...gTd, textAlign: 'right' }}>{fmt(s.priceAmount)}</td>
                <td style={{ ...gTd, width: 160 }}>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    value={s.groupId || ''}
                    onChange={(v) => assignItem(s.key, v)}
                    options={groupOptions}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="dashed" icon={<PlusCircleOutlined />} onClick={addGroup} style={{ marginBottom: 12 }}>
        Add Melt Group
      </Button>

      {/* Group cards */}
      {groups.map((g) => {
        const agg = groupAggregate(g, sourceItems);
        return (
          <Card key={g.id} size="small" style={{ marginBottom: 12, background: '#fff7f5', borderColor: '#ffd6cc' }}
            title={(
              <Row justify="space-between" align="middle">
                <Col><Tag color="volcano">Merged</Tag> {agg.members.length} item(s)</Col>
                <Col><Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeGroup(g.id)}>Remove group</Button></Col>
              </Row>
            )}>
            <Row gutter={[12, 8]} align="bottom">
              <Col xs={24} md={7}>
                <Text type="secondary" style={{ fontSize: 11 }}>Label</Text>
                <Input size="small" value={g.label} onChange={(e) => updateGroup(g.id, { label: e.target.value })} />
              </Col>
              <Col xs={8} md={5}>
                <Text type="secondary" style={{ fontSize: 11 }}>Melting Loss (g)</Text>
                <InputNumber size="small" min={0} precision={3} style={{ width: '100%' }}
                  value={g.meltingLoss} onChange={(v) => updateGroup(g.id, { meltingLoss: v })} />
              </Col>
              <Col xs={8} md={5}>
                <Text type="secondary" style={{ fontSize: 11 }}>Purity %</Text>
                <InputNumber size="small" min={0} max={100} precision={2} style={{ width: '100%' }}
                  value={g.purityPercent} onChange={(v) => updateGroup(g.id, { purityPercent: v })} />
              </Col>
            </Row>
            <Row gutter={12} style={{ marginTop: 8 }}>
              <Col span={6}><Stat label="Source Gross" value={`${agg.sourceGross.toFixed(3)} g`} /></Col>
              <Col span={6}><Stat label="Merged Gross" value={`${agg.mergedGross.toFixed(3)} g`} /></Col>
              <Col span={6}><Stat label="Net Weight" value={`${agg.netWeight.toFixed(4)} g`} /></Col>
              <Col span={6}><Stat label="Price" value={fmt(agg.priceAmount)} /></Col>
            </Row>
            {agg.members.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                Melting: {agg.members.map((m) => m.label).join(' + ')}
              </div>
            )}
          </Card>
        );
      })}

      <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
        Output: <strong>{groups.length}</strong> merged + <strong>{standaloneCount}</strong> standalone ={' '}
        <strong>{groups.length + standaloneCount}</strong> item(s) in this batch.
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#999' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

const gTh = { padding: '5px 8px', borderBottom: '2px solid #f0f0f0', textAlign: 'center', fontWeight: 600, fontSize: 11 };
const gTd = { padding: '5px 8px', fontSize: 12 };
